import React, { useState, useEffect } from "react";
import { Button, Form, Spinner, Row, Col, Container } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { createTheme, ThemeProvider } from "@mui/material";
import { useHistory } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "libphonenumber-js";
import firebase from "../config/firebase";
import { toast } from "react-toastify";
import InputFields from "./InputFields";
import AuthHeader from "./AuthHeader";
import { signupLoader } from "../store/actions/authAction";
import { LOGIN } from "../store/types";
import "../assets/css/style.css";

const PhoneAuth = () => {
  const { darkMode, color } = useSelector((state) => state.global);
  const { loading } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const history = useHistory();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("phone");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const inputColor = darkMode ? "#ffffff" : "rgb(101 92 92)";
  const labelProps = {
    style: {
      color: darkMode ? "#ffffff" : "rgb(101 92 92)",
    },
  };

  const theme = createTheme({
    palette: {
      secondary: {
        main: `${color ? color : "#000000"}`,
      },
    },
  });

  useEffect(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
        "recaptcha-container",
        {
          size: "invisible",
          callback: (response) => {},
          "expired-callback": () => {
            toast.error("reCAPTCHA expired. Please try again.");
          },
        }
      );
    }

    return () => {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const validatePhoneNumber = (phone) => {
    try {
      return phone && isValidPhoneNumber(phone);
    } catch (error) {
      return false;
    }
  };

  const sendOTP = async () => {
    if (!phoneNumber) {
      toast.error("Please enter a phone number");
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      toast.error("Please enter a valid phone number");
      return;
    }

    dispatch(signupLoader(true));

    try {
      const appVerifier = window.recaptchaVerifier;

      const confirmationResult = await firebase
        .auth()
        .signInWithPhoneNumber(phoneNumber, appVerifier);
      setConfirmationResult(confirmationResult);
      setStep("otp");
      setCountdown(60);
      toast.success("OTP sent successfully!");
      dispatch(signupLoader(false));
    } catch (error) {
      dispatch(signupLoader(false));
      console.error("Error sending OTP:", error);

      if (error.code === "auth/too-many-requests") {
        toast.error("Too many requests. Please try again later.");
      } else if (error.code === "auth/invalid-phone-number") {
        toast.error("Invalid phone number format");
      } else {
        toast.error("Failed to send OTP. Please try again.");
      }
    }
  };

  const resendOTP = async () => {
    if (countdown > 0) return;

    setIsResending(true);
    try {
      const appVerifier = window.recaptchaVerifier;

      const confirmationResult = await firebase
        .auth()
        .signInWithPhoneNumber(phoneNumber, appVerifier);
      setConfirmationResult(confirmationResult);
      setCountdown(60);
      toast.success("OTP resent successfully!");
    } catch (error) {
      console.error("Error resending OTP:", error);
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp.trim()) {
      toast.error("Please enter the OTP");
      return;
    }

    if (otp.length !== 6) {
      toast.error("OTP must be 6 digits");
      return;
    }

    dispatch(signupLoader(true));

    try {
      const result = await confirmationResult.confirm(otp);
      const user = result.user;

      const userDoc = await firebase
        .firestore()
        .collection("users")
        .doc(user.uid)
        .get();

      if (!userDoc.exists) {
        // Create new user document
        await firebase.firestore().collection("users").doc(user.uid).set({
          name: "",
          email: "",
          contactNumber: phoneNumber,
          type: "customer",
          onetimelogin: true,
          createdAt: firebase.firestore.Timestamp.now(),
          imageURL: "",
          address: "",
          gender: "",
          age: "",
          DOB: "",
        });
        toast.success("Account created successfully!");
      }

      // Get updated user data and login
      const updatedUserDoc = await firebase
        .firestore()
        .collection("users")
        .doc(user.uid)
        .get();
      const userData = updatedUserDoc.data();

      if (userData?.type === "customer") {
        if (userData.isDeleted) {
          toast.warning("Your account has been deleted. Please contact admin.");
        } else {
          dispatch({
            type: LOGIN,
            payload: {
              uid: user.uid,
              user: { id: user.uid, ...userData },
            },
          });

          const cartPath = localStorage.getItem("cartPath");
          cartPath ? history.push("/cart") : history.push("/restaurants");
          toast.success("Login successful!");
        }
      } else {
        toast.error("Invalid user type");
      }

      dispatch(signupLoader(false));
    } catch (error) {
      dispatch(signupLoader(false));
      console.error("Error verifying OTP:", error);

      if (error.code === "auth/invalid-verification-code") {
        toast.error("Invalid OTP. Please check and try again.");
      } else if (error.code === "auth/code-expired") {
        toast.error("OTP has expired. Please request a new one.");
        setStep("phone");
        setOtp("");
      } else {
        toast.error("Verification failed. Please try again.");
      }
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (step === "phone") {
        sendOTP();
      } else {
        verifyOTP();
      }
    }
  };

  const goBack = () => {
    if (step === "otp") {
      setStep("phone");
      setOtp("");
      setConfirmationResult(null);
    } else {
      history.push("/login");
    }
  };

  return (
    <Container className="py-5">
      <Row className="d-flex flex-column justify-content-center align-items-center">
        <Form
          className={darkMode ? "px-3 pay-with-card" : "px-3"}
          onSubmit={(e) => {
            e.preventDefault();
            step === "phone" ? sendOTP() : verifyOTP();
          }}
          onKeyDown={handleKeyDown}
        >
          <Col className="mb-5">
            <AuthHeader
              darkMode={darkMode}
              title={step === "phone" ? "Phone Login" : "Verify OTP"}
            />
          </Col>
          <Col>
            <ThemeProvider theme={theme}>
              {step === "phone" ? (
                <>
                  <div className="mb-4">
                    <label style={labelProps} className="form-label mb-2">
                      Enter Phone Number
                    </label>
                    <div className="phone-input-wrapper">
                      <PhoneInput
                        international
                        countryCallingCodeEditable={false}
                        defaultCountry="US"
                        value={phoneNumber}
                        onChange={setPhoneNumber}
                        placeholder="Enter phone number"
                        className="custom-phone-input"
                      />
                    </div>
                  </div>
                  <div
                    className="mt-2 mb-4"
                    style={{ color: inputColor, fontSize: "0.9em" }}
                  >
                    Select your country and enter your phone number
                  </div>
                  {/* reCAPTCHA will appear here */}
                  <div id="recaptcha-container" className="mb-3"></div>
                  <Button
                    className="py-3 mt-4 w-100 auth__btn"
                    style={{ background: `${color}` }}
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? <Spinner size="md" /> : "Send OTP"}
                  </Button>
                </>
              ) : (
                <>
                  <div className="mb-3" style={{ color: inputColor }}>
                    OTP sent to: <strong>{phoneNumber}</strong>
                  </div>
                  <InputFields
                    labelName="Enter 6-digit OTP"
                    type="text"
                    name="otp"
                    value={otp}
                    labelProps={labelProps}
                    inputColor={inputColor}
                    handleChangeData={handleOtpChange}
                    darkMode={darkMode}
                    placeholder="123456"
                    maxLength="6"
                  />
                  <Button
                    className="py-3 mt-4 w-100 auth__btn"
                    style={{ background: `${color}` }}
                    type="submit"
                    disabled={loading || otp.length !== 6}
                  >
                    {loading ? <Spinner size="md" /> : "Verify OTP"}
                  </Button>
                  <div className="mt-3 text-center">
                    {countdown > 0 ? (
                      <span style={{ color: inputColor }}>
                        Resend OTP in {countdown}s
                      </span>
                    ) : (
                      <Button
                        color="link"
                        onClick={resendOTP}
                        disabled={isResending}
                        style={{
                          color: `${color}`,
                          textDecoration: "underline",
                        }}
                      >
                        {isResending ? "Resending..." : "Resend OTP"}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </ThemeProvider>
            <div className="mt-3 text-center">
              <Button
                color="link"
                onClick={goBack}
                style={{ color: inputColor }}
              >
                ←{" "}
                {step === "phone"
                  ? "Back to Login Options"
                  : "Change Phone Number"}
              </Button>
            </div>
          </Col>
        </Form>
      </Row>
      {/* Hidden reCAPTCHA container */}
      <div id="recaptcha-container"></div>
    </Container>
  );
};

export default PhoneAuth;
